import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { CartActions, StateWithMultiCart } from '@spartacus/cart/base/core';
import {
  AuthGuard,
  AuthRedirectService,
  AuthService,
  AuthStorageService,
  RoutingService,
  SemanticPathService,
} from '@spartacus/core';
import { Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { ShimanoAuthRedirectService } from 'src/app/service/auth/shimano-auth-redirect.service';
import { B2bunitService } from 'src/app/service/b2bunit/b2bunit.service';
import { BaseSiteConfigService, DISABLED_SITES } from 'src/app/service/base-site/base-site-config.service';
import { StorageService } from 'src/app/service/storage/storage.service';
import { ASM_ENABLED_LOCAL_STORAGE_KEY } from '../../../asm/asm-constant';

@Injectable({
  providedIn: 'root',
})
export class CustomAuthGuard extends AuthGuard {
  asmEnabled = this.storageService.getLocalStorageItem(ASM_ENABLED_LOCAL_STORAGE_KEY);
  constructor(
    authService: AuthService,
    authRedirectService: AuthRedirectService,
    router: Router,
    semanticPathService: SemanticPathService,
    private shimanoAuthRedirectService: ShimanoAuthRedirectService,
    private baseSiteConfigService: BaseSiteConfigService,
    protected routingService: RoutingService,
    protected authStorage: AuthStorageService,
    private b2bUnitService: B2bunitService,
    private storageService: StorageService,
    private store: Store<StateWithMultiCart>
  ) {
    super(authService, authRedirectService, router, semanticPathService);
  }

  canActivate(): Observable<boolean> {
    return combineLatest([
      this.authService.isUserLoggedIn(),
      this.authStorage.getToken(),
      this.isCurrentPageDisabled$(),
      this.b2bUnitService.getShimanoUnitSelectionInfo$(),
    ]).pipe(
      map(([isLogged, token, isCurrentPageDisabled, unitSelection]) => {
        let unitSelectionEnabled = unitSelection === 'true';
        let shimanoSessionB2BUnit = this.storageService.getLocalStorageItem('shimanoSessionB2BUnit') ? true : false;
        this.storageService.setLocalStorageitem(
          'shimanoSessionB2BUnit_enabled',
          unitSelection === 'true' ? 'true' : 'false'
        );
        let b2bunitMultiple = this.storageService.getLocalStorageItem('shimanoSessionB2BUnit_multiple');
        let shimanoSessionB2BUnit_multiple = b2bunitMultiple === 'true';

        if (!token.access_token && !isLogged) {
          this.shimanoAuthRedirectService.reportAuthGuard();
          this.store.dispatch(new CartActions.ClearCartState()); // Clears cart state to prevent blank/white page after login
          this.routingService.go({ cxRoute: 'login' });
        } else {
          if (
            unitSelectionEnabled &&
            !shimanoSessionB2BUnit &&
            !this.unitSelection() &&
            !this.asmEnabled &&
            shimanoSessionB2BUnit_multiple
          ) {
            this.routingService.go({ cxRoute: 'unitSelection' });
          } else {
            if (isCurrentPageDisabled) {
              this.routingService.go({ cxRoute: 'notFound' });
            }
          }
        }
        return !!token.access_token && !isCurrentPageDisabled;
      })
    );
  }

  unitSelection(): boolean {
    const navigation = this.router.getCurrentNavigation();
    const serializedUrl = this.router.serializeUrl(navigation.finalUrl);
    return serializedUrl.includes('/unit-selection');
  }

  private getAbsoluteUrl(localUrl: string[]) {
    const url = localUrl.join('/');
    return url.startsWith('/') ? url : '/' + url;
  }

  private isCurrentPageDisabled$(): Observable<boolean> {
    return this.baseSiteConfigService.getBaseSiteConfigValues$(DISABLED_SITES).pipe(
      map((configData) => {
        const navigation = this.router.getCurrentNavigation();
        const serializedUrl = this.router.serializeUrl(navigation.finalUrl);

        // get localUrl from `businessType/country/language/localUrl`
        const localUrl = serializedUrl
          .split('/')
          .map((context, index) => (index >= 3 ? context : null))
          .filter(Boolean);

        return configData[0].value.split(',').includes(this.getAbsoluteUrl(localUrl));
      })
    );
  }
}
